import type { ModuleDetail, TeacherHome } from "../types";

// Everything a teacher needs to keep learning without a connection lives in
// two Cache Storage buckets: module/home JSON snapshots, and the media files
// those modules point at (keyed by their original URL, so a renderer can
// look a file up by the same URL the API gave it). Cache Storage is
// disk-backed and holds hundreds of MB, unlike localStorage.
//
// There is no service worker, so this only helps once the app has loaded:
// a teacher who loses signal mid-session keeps working, but a cold start
// with no connection still needs the network to fetch the app itself.
const DATA_CACHE = "cpd-offline-data-v1";
const MEDIA_CACHE = "cpd-offline-media-v1";

// Media is keyed by URL alone. Cloud Storage answers with "Vary: Origin",
// which would otherwise make a saved file look missing and download again.
const MEDIA_MATCH: CacheQueryOptions = { ignoreVary: true };

const dataKey = (name: string) => `${window.location.origin}/__offline/${name}`;

// Without this the browser may evict saved modules (and the answer queue in
// IndexedDB) when the phone runs low on space. Chrome grants it silently to
// sites the user engages with; asking again once granted is a no-op.
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export function offlineSupported(): boolean {
  return typeof window !== "undefined" && "caches" in window;
}

async function readJSON<T>(name: string): Promise<T | null> {
  if (!offlineSupported()) return null;
  try {
    const cache = await caches.open(DATA_CACHE);
    const hit = await cache.match(dataKey(name));
    return hit ? ((await hit.json()) as T) : null;
  } catch {
    return null;
  }
}

async function writeJSON(name: string, value: unknown): Promise<void> {
  if (!offlineSupported()) return;
  try {
    const cache = await caches.open(DATA_CACHE);
    await cache.put(
      dataKey(name),
      new Response(JSON.stringify(value), { headers: { "Content-Type": "application/json" } })
    );
  } catch {
    // Quota exceeded or storage blocked: the app still works online.
  }
}

export const readCachedHome = () => readJSON<TeacherHome>("home");
export const writeCachedHome = (home: TeacherHome) => writeJSON("home", home);
export const readCachedModule = (id: string) => readJSON<ModuleDetail>(`module/${id}`);
export const writeCachedModule = (module: ModuleDetail) => writeJSON(`module/${module.id}`, module);

export async function updateCachedModule(
  id: string,
  update: (module: ModuleDetail) => ModuleDetail
): Promise<void> {
  const current = await readCachedModule(id);
  if (current) await writeCachedModule(update(current));
}

export async function hasMedia(url: string): Promise<boolean> {
  if (!offlineSupported()) return false;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    return Boolean(await cache.match(url, MEDIA_MATCH));
  } catch {
    return false;
  }
}

export async function readMedia(url: string): Promise<Blob | null> {
  if (!offlineSupported()) return null;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    const hit = await cache.match(url, MEDIA_MATCH);
    return hit ? await hit.blob() : null;
  } catch {
    return null;
  }
}

// Downloads one file into the media cache, reporting bytes as they arrive.
// The body is teed: one branch streams straight into Cache Storage, the
// other is only read to count bytes, so a 500 MB video is never held in
// memory at once.
export async function downloadMedia(
  url: string,
  onBytes: (loaded: number, total: number) => void
): Promise<void> {
  const response = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
  const total = Number(response.headers.get("content-length")) || 0;
  const [toCache, toCount] = response.body.tee();
  const cache = await caches.open(MEDIA_CACHE);
  const stored = cache.put(url, new Response(toCache, { status: 200, headers: response.headers }));
  const reader = toCount.getReader();
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    loaded += value.byteLength;
    onBytes(loaded, total);
  }
  await stored;
}

// Drops media and module snapshots no longer referenced by any module the
// teacher can still open, so finished PD Days don't pile up on the phone.
export async function pruneOffline(keepModuleIDs: string[], keepURLs: string[]): Promise<void> {
  if (!offlineSupported()) return;
  try {
    const data = await caches.open(DATA_CACHE);
    const keepData = new Set([
      dataKey("home"),
      ...keepModuleIDs.map((id) => dataKey(`module/${id}`)),
    ]);
    for (const request of await data.keys()) {
      if (!keepData.has(request.url)) await data.delete(request);
    }
    const media = await caches.open(MEDIA_CACHE);
    const keepMedia = new Set(keepURLs.map((url) => new URL(url, window.location.href).href));
    for (const request of await media.keys()) {
      if (!keepMedia.has(request.url)) await media.delete(request);
    }
  } catch {
    // Best effort.
  }
}

// Called on sign-out: the next person on a shared handset must not see the
// previous teacher's modules.
export async function clearOffline(): Promise<void> {
  if (!offlineSupported()) return;
  await Promise.all([caches.delete(DATA_CACHE), caches.delete(MEDIA_CACHE)]).catch(() => undefined);
}
